"use client";

import { useState } from "react";
import { createClient } from "@/lib/supabase/client";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmSent, setConfirmSent] = useState(false);

  if (!isOpen) return null;

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const supabase = createClient();
    if (!supabase) { setError("Auth not configured"); setLoading(false); return; }

    if (mode === "signup") {
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) {
        setError(error.message);
      } else {
        setConfirmSent(true);
      }
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setError(error.message);
      } else {
        onClose();
      }
    }

    setLoading(false);
  };

  const handleGoogle = async () => {
    setError(null);
    const supabase = createClient();
    if (!supabase) { setError("Auth not configured"); return; }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) setError(error.message);
  };

  const reset = () => {
    setEmail("");
    setPassword("");
    setError(null);
    setConfirmSent(false);
  };

  const switchMode = () => {
    setMode(mode === "signin" ? "signup" : "signin");
    reset();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-sm mx-4 bg-panel-bg border border-panel-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-brand-linen">
              {mode === "signin" ? "Sign in" : "Create account"}
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-brand-muted hover:text-brand-linen hover:bg-brand-linen/5 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 4L12 12M12 4L4 12" />
              </svg>
            </button>
          </div>
          <p className="text-xs text-brand-muted">
            {mode === "signin"
              ? "Sign in to save and manage your plots"
              : "Create an account to get started"}
          </p>
        </div>

        {confirmSent ? (
          <div className="px-6 pb-6">
            <div className="bg-positive/10 border border-positive/20 rounded-xl p-4 text-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" className="mx-auto mb-2">
                <path d="M9 12L11 14L15 10" /><circle cx="12" cy="12" r="10" />
              </svg>
              <p className="text-sm text-positive font-medium">Check your email</p>
              <p className="text-xs text-brand-muted mt-1">
                We sent a confirmation link to {email}
              </p>
            </div>
            <button
              onClick={() => { setMode("signin"); reset(); }}
              className="w-full mt-3 text-xs text-brand-muted hover:text-brand-linen transition-colors"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <div className="px-6 pb-6">
            {/* Google button */}
            <button
              onClick={handleGoogle}
              className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-xl bg-brand-linen/[0.05] border border-panel-border hover:bg-brand-linen/[0.08] hover:border-brand-border transition-all text-sm font-medium text-brand-linen"
            >
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-panel-border" />
              <span className="text-[10px] text-brand-muted uppercase tracking-widest">or</span>
              <div className="flex-1 h-px bg-panel-border" />
            </div>

            {/* Email form */}
            <form onSubmit={handleEmailAuth} className="space-y-3">
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  required
                  className="w-full px-3.5 py-2.5 rounded-xl bg-panel-surface border border-panel-border text-sm text-brand-linen placeholder:text-brand-muted focus:outline-none focus:border-brand-meadow/50 focus:ring-1 focus:ring-brand-meadow/20 transition-all"
                />
              </div>
              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  minLength={6}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-panel-surface border border-panel-border text-sm text-brand-linen placeholder:text-brand-muted focus:outline-none focus:border-brand-meadow/50 focus:ring-1 focus:ring-brand-meadow/20 transition-all"
                />
              </div>

              {error && (
                <div className="px-3 py-2 rounded-lg bg-negative/10 border border-negative/20 text-xs text-negative">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2.5 rounded-xl bg-brand-meadow hover:bg-brand-meadow disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-semibold text-brand-linen"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    {mode === "signin" ? "Signing in..." : "Creating account..."}
                  </span>
                ) : (
                  mode === "signin" ? "Sign in" : "Create account"
                )}
              </button>
            </form>

            {/* Mode toggle */}
            <p className="mt-4 text-center text-xs text-brand-muted">
              {mode === "signin" ? "Don't have an account? " : "Already have an account? "}
              <button
                onClick={switchMode}
                className="text-brand-meadow hover:text-brand-sage transition-colors font-medium"
              >
                {mode === "signin" ? "Sign up" : "Sign in"}
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
